import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App';
import { AuthProvider } from './lib/auth';
import { I18nProvider } from './lib/i18n';
import { ToastProvider } from './components/ui/toast';
import { ApiError } from './lib/api';
import { installFriendlyValidationMessages } from '@his/shared';
import { followThemeAcrossTabs } from './lib/theme';
import { followBranchAcrossTabs } from './lib/branch';
import { installScrollContainers } from './lib/scroll-containers';
import './index.css';

// Forms validate against the same schemas the API uses; this decides what a
// failed field says to the person filling it in.
installFriendlyValidationMessages();
followThemeAcrossTabs();
followBranchAcrossTabs();
installScrollContainers();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Clinical screens must not show stale data, but a desk that switches
      // between tabs should not refetch everything on every focus either.
      staleTime: 15_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Permission, licensing and validation failures will not fix themselves.
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <I18nProvider>
            <ToastProvider>
              <App />
            </ToastProvider>
          </I18nProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
